/**
 * Admin page — manage seller (sub-user) accounts.
 *
 * Sellers can log in to the same MiNegocio app but see only the simplified
 * seller layout ("Tomar pedido" + "Mis pedidos"). This page lets the admin
 * create accounts, change passwords and delete sellers.
 */
import { useEffect, useState } from "react";
import { api } from "../App";
import { toast } from "sonner";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "../components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { Plus, Edit, Trash2, UserCog, KeyRound, Users, Activity } from "lucide-react";
import { PageLoader } from "../components/Spinner";
import { EmptyState } from "../components/EmptyState";
import AuditLog from "./AuditLog";

const emptyForm = { email: "", password: "", display_name: "" };

export default function Sellers({ embedded = false } = {}) {
  const [sellers, setSellers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const fetchSellers = async () => {
    try {
      const { data } = await api.get("/admin/sellers");
      setSellers(data);
    } catch {
      toast.error("Error al cargar vendedores");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchSellers(); }, []);

  const openNew = () => {
    setEditing(null);
    setForm(emptyForm);
    setDialogOpen(true);
  };

  const openEdit = (seller) => {
    setEditing(seller);
    setForm({ email: seller.email, display_name: seller.display_name, password: "" });
    setDialogOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        const payload = { display_name: form.display_name };
        if (form.password) payload.password = form.password;
        await api.put(`/admin/sellers/${editing.id}`, payload);
        toast.success("Vendedor actualizado");
      } else {
        await api.post("/admin/sellers", {
          email: form.email,
          password: form.password,
          display_name: form.display_name,
        });
        toast.success("Vendedor creado");
      }
      setDialogOpen(false);
      fetchSellers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al guardar");
    }
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/admin/sellers/${deleteTarget.id}`);
      toast.success("Vendedor eliminado");
      setDeleteTarget(null);
      fetchSellers();
    } catch {
      toast.error("Error al eliminar");
    }
  };

  if (loading) return <PageLoader label="Cargando vendedores…" testid="sellers-loader" />;

  return (
    <div className={embedded ? "" : "page-fade"}>
      {!embedded && (
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 gap-3">
          <div>
            <h2 className="text-2xl font-bold flex items-center gap-2" style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}>
              <UserCog className="h-6 w-6 text-[#002FA7]" />
              Vendedores
            </h2>
            <p className="text-sm text-slate-600 mt-1">
              Creá cuentas para tus vendedores. Cada uno verá sólo "Tomar pedido" y "Mis pedidos".
            </p>
          </div>
          <Button onClick={openNew} className="btn-primary rounded-sm" data-testid="new-seller-button">
            <Plus className="h-4 w-4 mr-2" /> Nuevo vendedor
          </Button>
        </div>
      )}

      {embedded && (
        <div className="flex justify-end mb-3">
          <Button onClick={openNew} size="sm" className="btn-primary rounded-sm" data-testid="new-seller-button">
            <Plus className="h-4 w-4 mr-2" /> Nuevo vendedor
          </Button>
        </div>
      )}

      {sellers.length === 0 ? (
        <EmptyState
          icon={Users}
          title="Aún no hay vendedores"
          description="Cuando crees el primero, va a poder iniciar sesión con su email y tomar pedidos en tu nombre."
          actionLabel="Crear primer vendedor"
          actionOnClick={openNew}
          testid="sellers-empty-state"
        />
      ) : (
        <Card className="bg-white border border-slate-200 rounded-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50">
                  <th className="text-left py-3 px-4 font-medium text-slate-600">Nombre</th>
                  <th className="text-left py-3 px-4 font-medium text-slate-600">Email</th>
                  <th className="text-left py-3 px-4 font-medium text-slate-600">Creado</th>
                  <th className="text-right py-3 px-4 font-medium text-slate-600">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {sellers.map((s) => (
                  <tr key={s.id} className="border-b border-slate-100 hover:bg-slate-50/60 transition-colors" data-testid={`seller-row-${s.id}`}>
                    <td className="py-3 px-4 font-medium text-slate-900">{s.display_name}</td>
                    <td className="py-3 px-4 text-slate-700">{s.email}</td>
                    <td className="py-3 px-4 text-xs text-slate-500">{s.created_at?.slice(0, 10)}</td>
                    <td className="py-3 px-4 text-right">
                      <Button size="sm" variant="ghost" onClick={() => openEdit(s)} data-testid={`edit-seller-${s.id}`}>
                        <Edit className="h-3.5 w-3.5" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => setDeleteTarget(s)} className="text-red-600 hover:text-red-700 hover:bg-red-50" data-testid={`delete-seller-${s.id}`}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editing ? "Editar vendedor" : "Nuevo vendedor"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4 mt-2">
            <div>
              <label className="block text-sm font-medium mb-1.5">Nombre de pantalla *</label>
              <Input
                required
                value={form.display_name}
                onChange={(e) => setForm({ ...form, display_name: e.target.value })}
                placeholder="Ej: Ana Solís"
                className="rounded-sm"
                data-testid="seller-display-name-input"
              />
              <p className="text-[11px] text-slate-500 mt-1">
                Este nombre aparece en sus pedidos. Debe coincidir con <code>vendedor_asignado</code> de los clientes que vea.
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1.5">Email *</label>
              <Input
                required
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                disabled={!!editing}
                placeholder="vendedor@empresa.com"
                className="rounded-sm"
                data-testid="seller-email-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1.5">
                {editing ? "Nueva contraseña (opcional)" : "Contraseña *"}
              </label>
              <Input
                type="password"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                required={!editing}
                minLength={6}
                placeholder={editing ? "Dejá vacío para mantener" : "Mínimo 6 caracteres"}
                className="rounded-sm"
                data-testid="seller-password-input"
              />
              {editing && (
                <p className="text-[11px] text-slate-500 mt-1 flex items-center gap-1">
                  <KeyRound className="h-3 w-3" />
                  Si dejás vacío, conservás la contraseña actual.
                </p>
              )}
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
              <Button type="submit" className="btn-primary rounded-sm" data-testid="save-seller-button">
                {editing ? "Guardar" : "Crear vendedor"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar vendedor?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteTarget?.display_name} no podrá iniciar sesión más. Sus pedidos anteriores quedan en el sistema.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-red-600 hover:bg-red-700 text-white">
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Actividad de vendedores — solo cuando se usa como página standalone.
          Cuando va embebido dentro de MyCompany, el AuditLog general del final
          de esa página ya cubre toda la actividad y evitamos duplicación. */}
      {!embedded && (
        <Card className="bg-white border border-slate-200 rounded-sm p-6 mt-6" data-testid="sellers-actividad-section">
          <div className="flex items-center gap-2 mb-2">
            <Activity className="h-5 w-5 text-[rgb(0,47,167)]" />
            <h3 className="text-lg font-semibold" style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}>
              Actividad de vendedores
            </h3>
          </div>
          <p className="text-xs text-slate-500 mb-4">
            Todo lo que tus vendedores hicieron: pedidos creados, productos agregados, pagos registrados. El filtro de usuario te permite acotar a un vendedor específico.
          </p>
          <AuditLog embedded fixedFilters={{ actor_role: "seller" }} />
        </Card>
      )}
    </div>
  );
}
